<template>
  <div class="mt-2 px-6 approvals-library-list-container">
    <LibrariesSkeletonLoader
      class="ml-6 mr-6"
      :is-loading="libraryLoading"
      is-requisition
    />

    <div v-if="!libraryLoading">
      <NoDataBlock
        v-if="showNoDataBlock"
        label="Approvals"
        document-type="Dashboard"
      >
        <template #description>
          There are no approval flows available for your selection.
        </template>
      </NoDataBlock>
      <ApprovalsLibraryTable v-else />
    </div>
  </div>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import LibrariesSkeletonLoader
  from '@/components/shared/SkeletonLoaders/LibrariesSkeletonLoader.vue';
import { useAppLoading } from '@/stores/app/loading';
import { computed, onMounted, onBeforeUnmount } from 'vue';
import { useApprovalsDashboardStore } from '@/stores/jobProfiles/approvalsDashboard';
import NoDataBlock from '@/components/library/NoDataBlock.vue';
import { subscribeToFilterUpdates, unsubscribeToFilterUpdates } from '@/helpers/filterDispatcher';
import { useFilters } from '@/stores/filters/filters';
import { useAppBase } from '@/stores/app/base';
import { useRoute, useRouter } from 'vue-router';
import { tooltipKeys } from '@/helpers/constants/tooltipKeys';
import { useTooltips } from '@/stores/insights/tooltips';
import ApprovalsLibraryTable from '@/components/library/approvals/ApprovalsLibraryTable.vue';

const emit = defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { pageLoading, listLoading } = storeToRefs(useAppLoading());
const { areFiltersApplied } = storeToRefs(useFilters());
const { flows, textFilter } = storeToRefs(useApprovalsDashboardStore());

const {
  setTextFilter,
  loadPage: loadApprovalsDashboardPage,
  resetSelectedStatus,
} = useApprovalsDashboardStore();

const { setFilterConfig } = useFilters();
const { showFilterBarSettings } = useAppBase();

const libraryLoading = computed(() => pageLoading.value || listLoading.value);

const hasFiltersOrSearch = computed(() => {
  const hasSearch = Boolean(route.query?.q);

  return hasSearch || areFiltersApplied.value;
});

const showNoDataBlock = computed(() => (
  !libraryLoading.value
  && flows.value.length === 0
  && !hasFiltersOrSearch.value
));

const loadPage = (params) => {
  const searchTerm = textFilter.value || params.searchTerm;

  const routeQuery = params.routeQuery || {
    ...params.selectedFilters,
    ...(params.page && { page: params.page }),
    ...(params.selectedMyJobs && { owned: params.selectedMyJobs }),
    ...(params.searchTerm && { q: params.searchTerm }),
  };

  loadApprovalsDashboardPage({
    ...params,
    routeQuery,
    searchTerm,
  });
};

onMounted(async () => {
  subscribeToFilterUpdates(loadPage);

  setFilterConfig({
    module: 'approvalsDashboard',
    endpoint: 'approvalsLibrary',
    datepicker: false,
    showMyJobs: true,
    ignorableFilters: ['status'],
  });

  showFilterBarSettings({ route: route.name });

  const searchTerm = route.query?.q;

  if (searchTerm) setTextFilter(searchTerm);

  await useTooltips().loadTooltips(tooltipKeys['approvals-library'] || []);

  loadPage({
    routeQuery: route.query || '',
    router,
    page: route.query?.page || 1,
    selectedMyJobs: route.query?.owned === 'true',
  });

  emit('track-page-load', { page: 'Approvals Dashboard: Open' });
});

onBeforeUnmount(() => {
  unsubscribeToFilterUpdates(loadPage);
  setTextFilter('');
  resetSelectedStatus(router);
  useApprovalsDashboardStore().reset();

  setFilterConfig({
    subMenuType: null,
    ignorableFilters: [],
  });
});
</script>
